import { useScrollReveal, useCountUp } from '@/hooks/useScrollAnimations';
import { Lightbulb, Shield, Award, Users, Zap, Handshake } from 'lucide-react';

const values = [
  { icon: Lightbulb, title: 'Innovation', desc: 'Pushing boundaries with cutting-edge techniques' },
  { icon: Shield, title: 'Integrity', desc: 'Honest dealings and transparent relationships' },
  { icon: Award, title: 'Excellence', desc: 'Uncompromising quality in every project' },
  { icon: Users, title: 'Collaboration', desc: 'Working together to achieve shared goals' },
  { icon: Zap, title: 'Impact', desc: 'Creating lasting impressions that matter' },
  { icon: Handshake, title: 'Partnership', desc: 'Building long-term client relationships' },
];

const stats = [
  { end: 50, label: 'Schools Served', suffix: '+' },
  { end: 100, label: 'Corporate Clients', suffix: '+' },
  { end: 12, label: 'Years Experience', suffix: '+' },
  { end: 5000, label: 'Projects Delivered', suffix: '+' },
];

const CoreValues = () => {
  const { ref, isVisible } = useScrollReveal();
  const { ref: statsRef, isVisible: statsVisible } = useScrollReveal();

  return (
    <section className="py-24 bg-muted/50">
      <div ref={ref} className="container mx-auto px-4">
        <div className={`text-center mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Our Foundation</span>
          <h2 className="text-3xl md:text-5xl font-bold font-display mt-3 text-foreground">Core Values</h2>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mb-20">
          {values.map((v, i) => (
            <div
              key={v.title}
              className={`text-center group transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: isVisible ? `${i * 80}ms` : '0ms' }}
            >
              <div className="w-16 h-16 rounded-2xl bg-card border border-border mx-auto mb-4 flex items-center justify-center group-hover:bg-primary group-hover:border-primary transition-all duration-300 group-hover:scale-110 shadow-sm">
                <v.icon className="w-7 h-7 text-primary group-hover:text-primary-foreground transition-colors" />
              </div>
              <h3 className="font-bold font-display text-foreground mb-1">{v.title}</h3>
              <p className="text-muted-foreground text-sm">{v.desc}</p>
            </div>
          ))}
        </div>

        {/* Stats */}
        <div ref={statsRef} className="grid grid-cols-2 md:grid-cols-4 gap-8 bg-secondary rounded-2xl p-10">
          {stats.map((s) => (
            <StatCounter key={s.label} {...s} visible={statsVisible} />
          ))}
        </div>
      </div>
    </section>
  );
};

const StatCounter = ({ end, label, suffix, visible }: { end: number; label: string; suffix: string; visible: boolean }) => {
  const count = useCountUp(end, 2000, visible);
  return (
    <div className="text-center">
      <div className="text-3xl md:text-5xl font-bold font-display text-primary-foreground">
        {count}{suffix}
      </div>
      <div className="text-primary-foreground/60 mt-2 text-sm font-medium">{label}</div>
    </div>
  );
};

export default CoreValues;
